/**
 * Upload Letterhead Dialog
 * Uploads a .docx letterhead template with a {{content}} placeholder.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Upload, Loader2, FileText, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';

const MAX_SIZE_BYTES = 5 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

interface UploadLetterheadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UploadLetterheadDialog({ open, onOpenChange }: UploadLetterheadDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setIsDefault(false);
    setFile(null);
    setFileError(null);
  };

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('No file selected');
      const fileBase64 = await fileToBase64(file);
      const resp = await apiClient.post('/automation/letterheads', {
        name,
        isDefault,
        fileBase64,
        originalFilename: file.name,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: (data: any) => {
      if (data?.hasContentPlaceholder === false) {
        toast.warning('Letterhead uploaded but no {{content}} placeholder was detected.');
      } else {
        toast.success('Letterhead uploaded.');
      }
      queryClient.invalidateQueries({ queryKey: ['automation', 'letterheads'] });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to upload letterhead');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileError(null);
    const f = e.target.files?.[0];
    if (!f) { setFile(null); return; }
    if (!f.name.toLowerCase().endsWith('.docx')) {
      setFileError('Only .docx files are supported');
      setFile(null);
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setFileError('File too large (max 5MB)');
      setFile(null);
      return;
    }
    setFile(f);
  };

  const canSubmit = !!name.trim() && !!file && !uploadMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Upload Letterhead</DialogTitle>
          <DialogDescription>
            Upload your firm's branded letterhead (header, footer, logo).
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 flex gap-2">
          <AlertTriangle className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground">
            Your letterhead should contain a <span className="font-mono text-blue-600">{'{{content}}'}</span> placeholder where the letter body will be inserted.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="lh-name">Name *</Label>
            <Input id="lh-name" placeholder="e.g. Firm Letterhead 2026" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="lh-default" checked={isDefault} onCheckedChange={(v) => setIsDefault(Boolean(v))} />
            <Label htmlFor="lh-default" className="cursor-pointer">Set as default letterhead</Label>
          </div>

          <div className="space-y-2">
            <Label>Word File (.docx) *</Label>
            {file ? (
              <div className="flex items-center gap-2 rounded-md border p-2 bg-muted/30">
                <FileText className="h-4 w-4 text-blue-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                </div>
                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label className={cn(
                'flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-md py-6 cursor-pointer hover:bg-muted/40 transition-colors',
                fileError && 'border-destructive'
              )}>
                <Upload className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Click to select a .docx file</span>
                <span className="text-[11px] text-muted-foreground">Max 5MB</span>
                <input type="file" accept=".docx" className="hidden" onChange={handleFileChange} />
              </label>
            )}
            {fileError && <p className="text-xs text-destructive">{fileError}</p>}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploadMutation.isPending}>Cancel</Button>
          <Button onClick={() => uploadMutation.mutate()} disabled={!canSubmit}>
            {uploadMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Upload Letterhead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default UploadLetterheadDialog;
